import React, { useState, useEffect, useRef } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonIcon,
  IonText,
  IonAlert,
  IonSpinner,
  IonTextarea,
  IonRadioGroup,
  IonRadio,
  IonLabel,
  IonItem,
  IonCheckbox,
} from '@ionic/react';
import Toaster, { ToasterVariant } from '@/components/common/Toaster';
import {
  closeOutline,
  calendarOutline,
  personOutline,
  locationOutline,
  cloudDoneOutline,
  informationCircleOutline,
  chevronBack,
  chevronForward,
  trashOutline,
  checkmarkCircleOutline,
  thumbsUpOutline,
  addCircleOutline,
  checkmark,
  arrowBack
} from 'ionicons/icons';
import { InspectionModalData, InspectionFormData, ChecklistItemFormData, FileAttachment, AttachmentType } from '../../../types/model/inspection-modal';
import { Attachment } from '../../../types/model/inspection';
import InspectionChecklist from './InspectionChecklist';
import InspectionDetailsDrawer from './InspectionDetailsDrawer';
import { apiService } from '../../../services/ApiService';
import inspectionService from '@/services/InspectionService';
import lookupDataService from '@/services/data/LookupDataService';
import permissionService from '@/services/PermissionService';
import AttachmentsSection from '@/components/common/AttachmentsSection';
import ModalFooter from '@/components/common/ModalFooter';

interface InspectionModalProps {
  isOpen: boolean;
  onDidDismiss: () => void;
  inspectionData: InspectionModalData | null;
  onDataUpdate?: (updatedData: InspectionModalData) => void;
  onDeleted?: () => void;
  onAddCorrectiveAction?: (inspectionData: InspectionModalData) => void;
}

const InspectionModal: React.FC<InspectionModalProps> = ({
  isOpen,
  onDidDismiss,
  inspectionData,
  onDataUpdate,
  onDeleted,
  onAddCorrectiveAction,
}) => {
  const [formData, setFormData] = useState<InspectionFormData>({
    comments: '',
    isExceptional: false,
    checklistItems: [],
    attachments: [],
  });
  const [originalFormData, setOriginalFormData] = useState<InspectionFormData>({
    comments: '',
    isExceptional: false,
    checklistItems: [],
    attachments: [],
  });
  const [loading, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [showExitAlert, setShowExitAlert] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<ToasterVariant>('success');
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [internalMode, setInternalMode] = useState<'edit' | 'delete' | 'view'>('edit');
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Actions panel state
  const [activeAction, setActiveAction] = useState<'complete' | 'approve' | 'add-ca' | null>(null);
  const [completeReason, setCompleteReason] = useState('');
  const [approvalOption, setApprovalOption] = useState<'1' | '2' | '0'>('1');
  const [approvalComments, setApprovalComments] = useState('');
  const [isActioning, setIsActioning] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(() => window.innerWidth < 992);
  
  // Refs for auto-save management
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastChangeTimeRef = useRef<Date | null>(null);
  const forceClosingRef = useRef(false);
  
  // Auto-save interval (30 seconds)
  const AUTO_SAVE_INTERVAL = 30 * 1000; // 30 seconds in milliseconds

  useEffect(() => {
    if (inspectionData && isOpen) {
      forceClosingRef.current = false;
      const nextMode = inspectionData.status === 'completed' ? 'view' : 'edit';
      setInternalMode(nextMode);
      setActiveAction(null);
      setCompleteReason('');
      setApprovalOption('1');
      setApprovalComments('');
      setIsDrawerOpen(false);
      initializeFormData();
      if (nextMode !== 'view') {
        startAutoSave();
      } else {
        stopAutoSave();
      }
    } else {
      stopAutoSave();
    }

    // Cleanup on unmount
    return () => {
      stopAutoSave();
    };
  }, [inspectionData, isOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const initializeFormData = () => {
    if (!inspectionData) return;

    const checklistItems: ChecklistItemFormData[] = inspectionData.checklistItems.map(item => ({
      id: item.Id,
      score: item.Score,
      comment: item.Comment || '',
      attachments: item.Attachments?.map(att => ({
        fileId: att.Id.toString(),
        fileName: att.Filename,
        link: att.Link  || '',
        description: att.Description  || '',
        type: getAttachmentType(att)
      })) || []
    }));

    const isExceptional = inspectionData.isExceptional ?? false;

    const attachments: FileAttachment[] = (inspectionData.attachments ?? []).map((attachment, index) => ({
      type: attachment.Link ? 'link' : 'existing-resource',
      index,
      attachmentId: attachment.Id,
      fileId: attachment.FileId ? attachment.FileId.toString() : undefined,
      description: attachment.Description || '',
      link: attachment.Link || '',
      fileName: attachment.Filename || undefined,
    }));

    const initialData = {
      comments: inspectionData.comments ?? '',
      isExceptional,
      checklistItems,
      attachments,
    };
    setFormData(initialData);
    setOriginalFormData(JSON.parse(JSON.stringify(initialData)));
    setLastAutoSave(new Date());
  };

  const getAttachmentType = (attachment: Attachment): AttachmentType => {
    let attachmentType: AttachmentType = 'local-file' ;
    if(!!attachment.Link) {
      attachmentType = 'link'
    } 
    return attachmentType;
  }

  const startAutoSave = () => {
    // Clear any existing interval
    stopAutoSave();
    
    // Set up auto-save interval
    autoSaveIntervalRef.current = setInterval(() => {
      performAutoSave();
    }, AUTO_SAVE_INTERVAL);
  };

  const stopAutoSave = () => {
    if (autoSaveIntervalRef.current) {
      clearInterval(autoSaveIntervalRef.current);
      autoSaveIntervalRef.current = null;
    }
  };

  const performAutoSave = async () => {
    console.log('Performing auto-save...');
    // Only auto-save if there are unsaved changes and not already saving
    if (!hasUnsavedChanges() || loading || autoSaving) {
      return;
    }

    // Check if enough time has passed since last change (debounce)
    const now = new Date();
    if (lastChangeTimeRef.current && now.getTime() - lastChangeTimeRef.current.getTime() < 10000) {
      // Wait at least 10 seconds after last change before auto-saving
      return;
    }

    console.log('Attempting auto-save...');
    setAutoSaving(true);
    try {
      await saveData(false); // false indicates auto-save (no toast notification)
      setLastAutoSave(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
      // Don't show error toast for auto-save failures to avoid interrupting user
    } finally {
      setAutoSaving(false);
    }
  };

  const hasUnsavedChanges = (): boolean => {
    return JSON.stringify(formData) !== JSON.stringify(originalFormData);
  };

  const saveData = async (showNotification = true): Promise<void> => {
    if (!inspectionData) return;

    console.log('Saving inspection data...', formData);

    // Save checklist items
    await inspectionService.saveInspection(inspectionData, formData, originalFormData);

    // Update original data to reflect saved state
    setOriginalFormData(JSON.parse(JSON.stringify(formData)));
    
    // Notify parent of data update
    if (onDataUpdate && inspectionData) {
      const updatedData = { ...inspectionData };
      updatedData.comments = formData.comments;
      updatedData.isExceptional = formData.isExceptional;
      // Update checklist items with new scores/comments
      updatedData.checklistItems = updatedData.checklistItems.map(item => {
        const formItem = formData.checklistItems.find(fi => fi.id === item.Id);
        if (formItem) {
          return {
            ...item,
            Score: formItem.score ?? null,
            Comment: formItem.comment || null,
          };
        }
        return item;
      });
      onDataUpdate(updatedData);
    }

    if (showNotification) {
      setToastMessage('Inspection data saved successfully');
      setToastVariant('success');
      setShowToast(true);
    }
  };

  const handleSave = async () => {
    if (!inspectionData) return;

    setSaving(true);
    try {
      await saveData(true);
      stopAutoSave();
      onDidDismiss();
    } catch (error) {
      console.error('Error saving inspection data:', error);
      setToastMessage('Failed to save inspection data');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (forceClosingRef.current) return;
    if (isReadOnlyMode) {
      stopAutoSave();
      onDidDismiss();
      return;
    }
    if (hasUnsavedChanges()) {
      setShowExitAlert(true);
    } else {
      stopAutoSave();
      onDidDismiss();
    }
  };

  const handleForceClose = () => {
    setShowExitAlert(false);
    stopAutoSave();
    forceClosingRef.current = true;
    onDidDismiss();
  };

  const handleDelete = async () => {
    if (!inspectionData) return;
    setIsDeleting(true);
    try {
      await apiService.deleteInspection(inspectionData.inspectionResultId);
      stopAutoSave();
      forceClosingRef.current = true;
      onDidDismiss();
      onDeleted?.();
    } catch (error) {
      console.error('Error deleting inspection:', error);
      setToastMessage('Failed to delete inspection');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsDeleting(false);
      setShowDeleteAlert(false);
    }
  };

  const trackChange = () => {
    lastChangeTimeRef.current = new Date();
  };

  const handleActionIconClick = (action: 'complete' | 'approve' | 'add-ca') => {
    if (activeAction === action) {
      setActiveAction(null);
    } else {
      setActiveAction(action);
      if (action === 'approve') {
        setApprovalComments(inspectionData?.approvalComments ?? '');
        setApprovalOption(inspectionData?.isApproved === true ? '1' : inspectionData?.isApproved === false ? '2' : '0');
      }
    }
  };

  const handleAddCorrectiveActionClick = () => {
    if (!inspectionData || !onAddCorrectiveAction) return;

    setActiveAction(null);
    stopAutoSave();
    forceClosingRef.current = true;
    onAddCorrectiveAction(inspectionData);
    onDidDismiss();
  };

  const handleMarkAsComplete = async () => {
    if (!inspectionData) return;

    setIsActioning(true);
    try {

      const inspectionDateTime = new Date(inspectionData.inspectionDate);
      if (inspectionDateTime.getTime() > Date.now()) {
        throw new Error('Date cannot be in the future.');
      }

      const request = {
        Id: inspectionData.inspectionResultId,
        Comments: formData.comments,
        LineItemId: inspectionData.lineItemId,
        IsExceptional: formData.isExceptional,
        ChecklistItems: formData.checklistItems.map(item => ({
          Id: item.id,
          Score: item.score ?? null,
          Comment: item.comment || null,
          Attachments: { Add: [], Edit: [], Delete: [] },
        })),
        InspectedOn: inspectionData.inspectionDate,
        SiteId: inspectionData.siteId,
        Attachments: { Add: [], Edit: [], Delete: [] },
        AlertDays: inspectionData.alertDays ?? 0,
        AlertEmails: inspectionData.alertEmails ?? '',
        IsInProgress: false,
        UserId: inspectionData.userId,
        ...(completeReason.trim() ? { Reason: completeReason.trim() } : {}),
      };
      await apiService.saveInspection(request);
      setActiveAction(null);
      setToastMessage('Inspection marked as complete');
      setToastVariant('success');
      setShowToast(true);
      stopAutoSave();
      forceClosingRef.current = true;
      onDataUpdate?.({ ...inspectionData, isInProgress: false });
      onDidDismiss();
    } catch (error) {
      console.error('Error marking inspection as complete:', error);
      setToastMessage('Failed to mark inspection as complete');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsActioning(false);
    }
  };

  const handleApproveInspection = async () => {
    if (!inspectionData) return;
    setIsActioning(true);
    const isApprovedValue = approvalOption === '1' ? true : approvalOption === '2' ? false : null;
    try {
      await apiService.approveInspection({
        Id: inspectionData.inspectionResultId,
        IsApproved: isApprovedValue,
        ApprovalComments: approvalComments.trim() || null,
      });
      setActiveAction(null);
      const label = isApprovedValue === true ? 'approved' : isApprovedValue === false ? 'disapproved' : 'approval cleared';
      setToastMessage(`Inspection ${label} successfully`);
      setToastVariant('success');
      setShowToast(true);
      stopAutoSave();
      forceClosingRef.current = true;
      onDataUpdate?.({ ...inspectionData, isApproved: isApprovedValue, approvalComments: approvalComments.trim() || null });
      onDidDismiss();
    } catch (error) {
      console.error('Error approving inspection:', error);
      setToastMessage('Failed to update inspection approval');
      setToastVariant('error');
      setShowToast(true);
    } finally {
      setIsActioning(false);
    }
  };

  const handleChecklistItemChange = (itemId: number, data: Partial<ChecklistItemFormData>) => {
    setFormData(prev => ({
      ...prev,
      checklistItems: prev.checklistItems.map(item =>
        item.id === itemId ? { ...item, ...data } : item
      ),
    }));
    trackChange();
  };

  const handleCommentsChange = (value: string) => {
    setFormData(prev => ({ ...prev, comments: value }));
    trackChange();
  };

  const handleExceptionalChange = (checked: boolean) => {
    setFormData(prev => ({ ...prev, isExceptional: checked }));
    trackChange();
  };

  const handleAddInspectionAttachment = (type: AttachmentType) => {
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, { type, index: prev.attachments.length }],
    }));
    trackChange();
  };

  const handleDeleteInspectionAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, currentIndex) => currentIndex !== index),
    }));
    trackChange();
  };

  const handleInspectionAttachmentFileUpdate = (index: number, file: File) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.map((attachment, currentIndex) =>
        currentIndex === index
          ? { ...attachment, file, fileName: file.name, size: file.size }
          : attachment
      ),
    }));
    trackChange();
  };

  const handleInspectionAttachmentLinkUpdate = (index: number, link: string) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.map((attachment, currentIndex) =>
        currentIndex === index ? { ...attachment, link } : attachment
      ),
    }));
    trackChange();
  };

  const handleInspectionAttachmentExistingFileSelect = (index: number, fileId: string) => {
    const selectedFile = inspectionData?.existingResources.find(file => file.Id.toString() === fileId);

    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.map((attachment, currentIndex) =>
        currentIndex === index
          ? { ...attachment, fileId, fileName: selectedFile?.Name }
          : attachment
      ),
    }));
    trackChange();
  };

  const handleInspectionAttachmentDescriptionUpdate = (index: number, description: string) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.map((attachment, currentIndex) =>
        currentIndex === index ? { ...attachment, description } : attachment
      ),
    }));
    trackChange();
  };


  const getAutoSaveStatus = () => {
    if (autoSaving) return 'Auto-saving...';
    if (!hasUnsavedChanges()) return 'All changes saved';
    if (lastAutoSave) {
      const timeSince = Math.floor((Date.now() - lastAutoSave.getTime()) / 60000);
      return `Last auto-saved ${timeSince} minute${timeSince !== 1 ? 's' : ''} ago`;
    }
    return 'Unsaved changes';
  };

  if (!inspectionData) return null;

  const isInspectionInProgress = inspectionData.isInProgress ?? true;
  const isReadOnlyMode = internalMode === 'view' || inspectionData.status === 'completed';
  const isInEditMode = internalMode === 'edit';
  const isAddCorrectiveActionDisabled =
    isReadOnlyMode ||
    !isInEditMode ||
    hasUnsavedChanges() ||
    isActioning ||
    !onAddCorrectiveAction;

  return (
    <>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <IonModal isOpen={isOpen} onDidDismiss={handleClose} style={{ '--height': '90%', '--width': '95%', '--max-width': '1200px', '--border-radius': '16px' } as React.CSSProperties}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{inspectionData.lineItem} {inspectionData.title}</IonTitle>
            <IonButtons slot="end">
              {/* Auto-save status indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }}>
                {autoSaving && <IonIcon icon={cloudDoneOutline} style={{ fontSize: '1rem', color: 'var(--ion-color-primary)', animation: 'spin 2s linear infinite' }} />}
                <IonText color="medium">
                  <small>{getAutoSaveStatus()}</small>
                </IonText>
              </div>
              {!isReadOnlyMode && internalMode === 'edit' && (
                <IonButton fill="clear" color="danger" onClick={() => setInternalMode('delete')}>
                  <IonIcon icon={trashOutline} />
                </IonButton>
              )}
              <IonButton fill="clear" onClick={handleClose}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent>
          {/* Modal Header Info + Actions Panel */}
          <div style={{ padding: '1rem', background: 'var(--ion-color-light-tint)', borderBottom: '1px solid var(--ion-color-light-shade)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start' }}>
              {/* Info fields — shrink to fit alongside actions panel */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', flex: '1 1 0', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <IonIcon icon={calendarOutline} style={{ fontSize: '1.25rem', color: 'var(--ion-color-primary)', flexShrink: 0 }} />
                  <div>
                    <IonText color="medium">
                      <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.85rem' }}>Inspection Date</p>
                    </IonText>
                    <p style={{ margin: 0, fontWeight: 500 }}>{new Date(inspectionData.inspectionDate).toLocaleDateString()}</p>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <IonIcon icon={personOutline} style={{ fontSize: '1.25rem', color: 'var(--ion-color-primary)', flexShrink: 0 }} />
                  <div>
                    <IonText color="medium">
                      <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.85rem' }}>Inspector</p>
                    </IonText>
                    <p style={{ margin: 0, fontWeight: 500 }}>{inspectionData.inspectorName}</p>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <IonIcon icon={locationOutline} style={{ fontSize: '1.25rem', color: 'var(--ion-color-primary)', flexShrink: 0 }} />
                  <div>
                    <IonText color="medium">
                      <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.85rem' }}>Site</p>
                    </IonText>
                    <p style={{ margin: 0, fontWeight: 500 }}>{inspectionData.siteName}</p>
                  </div>
                </div>
              </div>

              {/* Actions panel */}
              {!isReadOnlyMode && (permissionService.canMarkAsComplete() || permissionService.canApproveInspection() || (permissionService.canAddCA() && isInspectionInProgress)) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '0.5rem', flexShrink: 0 }}>
                  {/* Action icon buttons */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', borderLeft: '1px solid var(--ion-color-light-shade)', paddingLeft: '0.75rem' }}>
                    {permissionService.canMarkAsComplete() && (
                      <IonButton
                        style={{ margin: '0px'}}
                        fill="clear"
                        size="default"
                        title="Mark As Complete"
                        disabled={hasUnsavedChanges() || isActioning || !isInspectionInProgress}
                        onClick={() => handleActionIconClick('complete')}
                      >
                        <IonIcon
                          icon={checkmarkCircleOutline}
                          color={
                            hasUnsavedChanges() || isActioning || !isInspectionInProgress
                              ? 'medium'
                              : activeAction === 'complete'
                              ? 'primary'
                              : 'success'
                          }
                        />
                      </IonButton>
                    )}
                    {permissionService.canApproveInspection() && !isInspectionInProgress && (
                      <IonButton
                        style={{ margin: '0px'}}
                        fill="clear"
                        size="default"
                        title="Approve / Disapprove"
                        disabled={hasUnsavedChanges() || isActioning}
                        onClick={() => handleActionIconClick('approve')}
                      >
                        <IonIcon
                          icon={thumbsUpOutline}
                          color={
                            hasUnsavedChanges() || isActioning
                              ? 'medium'
                              : activeAction === 'approve'
                              ? 'primary'
                              : inspectionData.isApproved === true
                              ? 'success'
                              : inspectionData.isApproved === false
                              ? 'danger'
                              : 'warning'
                          }
                        />
                      </IonButton>
                    )}
                    {permissionService.canAddCA() && isInspectionInProgress && (
                      <IonButton
                        style={{ margin: '0px' }}
                        fill="clear"
                        size="default"
                        title="Add Corrective Action"
                        disabled={isAddCorrectiveActionDisabled}
                        onClick={() => handleActionIconClick('add-ca')}
                      >
                        <IonIcon
                          icon={addCircleOutline}
                          color={
                            isAddCorrectiveActionDisabled
                              ? 'medium'
                              : activeAction === 'add-ca'
                              ? 'primary'
                              : 'tertiary'
                          }
                        />
                      </IonButton>
                    )}
                  </div>
                  

                  {/* Inline confirmation box */}
                  {activeAction !== null && (
                    <div style={{
                      padding: '12px 16px',
                      backgroundColor: 'var(--ion-color-light)',
                      borderRadius: '8px',
                      border: '1px solid var(--ion-color-medium)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      minWidth: '260px',
                      maxWidth: '360px',
                    }}>
                      {activeAction === 'complete' && (
                        <>
                          <IonText>
                            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Mark As Complete</span>
                          </IonText>
                          {lookupDataService.getContractDetails()?.LogInspResMod && (
                            <IonTextarea
                              placeholder="Reason (required)"
                              value={completeReason}
                              onIonInput={e => setCompleteReason(e.detail.value ?? '')}
                              rows={2}
                              style={{ '--background': 'var(--ion-color-light-tint)', '--border-radius': '4px', fontSize: '0.875rem' }}
                            />
                          )}
                        </>
                      )}
                      {activeAction === 'approve' && (
                        <>
                          <IonText>
                            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Approve / Disapprove</span>
                          </IonText>
                          <IonRadioGroup value={approvalOption} onIonChange={e => setApprovalOption(e.detail.value as '1' | '2' | '0')}>
                            <div style={{ display: 'flex', gap: '1rem' }}>
                              <IonItem lines="none" style={{ '--min-height': '32px', '--padding-start': '0', '--inner-padding-end': '0' }}>
                                <IonRadio value="1" style={{ marginRight: '6px' }} />
                                <IonLabel style={{ fontSize: '0.875rem' }}>Approve</IonLabel>
                              </IonItem>
                              <IonItem lines="none" style={{ '--min-height': '32px', '--padding-start': '0', '--inner-padding-end': '0' }}>
                                <IonRadio value="2" style={{ marginRight: '6px' }} />
                                <IonLabel style={{ fontSize: '0.875rem' }}>Disapprove</IonLabel>
                              </IonItem>
                              <IonItem lines="none" style={{ '--min-height': '32px', '--padding-start': '0', '--inner-padding-end': '0' }}>
                                <IonRadio value="0" style={{ marginRight: '6px' }} />
                                <IonLabel style={{ fontSize: '0.875rem' }}>Clear</IonLabel>
                              </IonItem>
                            </div>
                          </IonRadioGroup>
                          <IonTextarea
                            placeholder="Comments (optional)"
                            value={approvalComments}
                            onIonInput={e => setApprovalComments(e.detail.value ?? '')}
                            rows={2}
                            style={{ '--background': 'var(--ion-color-light-tint)', '--border-radius': '4px', fontSize: '0.875rem' }}
                          />
                        </>
                      )}
                      {activeAction === 'add-ca' && (
                        <>
                          <IonText>
                            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Add Corrective Action</span>
                          </IonText>
                          <IonText color="medium">
                            <small>
                              This will close the inspection modal and open a new corrective action linked to this inspection.
                            </small>
                          </IonText>
                        </>
                      )}
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        {isActioning ? (
                          <IonSpinner name="crescent" />
                        ) : (
                          <>
                            <IonButton
                              size="small"
                              fill="solid"
                              color="primary"
                              disabled={
                                activeAction === 'complete' &&
                                (lookupDataService.getContractDetails()?.LogInspResMod ?? false) &&
                                !completeReason.trim()
                              }
                              onClick={
                                activeAction === 'complete'
                                  ? handleMarkAsComplete
                                  : activeAction === 'approve'
                                  ? handleApproveInspection
                                  : handleAddCorrectiveActionClick
                              }
                            >
                              OK
                            </IonButton>
                            <IonButton size="small" fill="outline" color="medium" onClick={() => setActiveAction(null)}>
                              Cancel
                            </IonButton>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              pointerEvents: internalMode === 'delete' ? 'none' : undefined,
              opacity: internalMode === 'delete' ? 0.7 : 1,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '1rem',
              padding: '1rem',
              alignItems: 'flex-start',
            }}
          >
            <div>
              <IonButton
                style={{ margin: '0' }}
                fill="clear"
                size="small"
                title={isDrawerOpen ? 'Hide inspection background details' : 'Show inspection background details'}
                onClick={() => setIsDrawerOpen(prev => !prev)}
              >
                <IonIcon icon={informationCircleOutline} slot="start" />
                <IonIcon icon={isDrawerOpen ? chevronBack : chevronForward} />
              </IonButton>
            </div>
            
            {isDrawerOpen && (
              <div
                style={{
                  width: isMobile ? '100%' : '340px',
                  minWidth: isMobile ? '100%' : '340px',
                  maxWidth: isMobile ? '100%' : '340px',
                  flexShrink: 0,
                }}
              >
                <InspectionDetailsDrawer
                  lineItemDetail={inspectionData.lineItemDetail}
                  inspectionPlanBackground={inspectionData.inspectionPlanBackground}
                />
              </div>
            )}

            <div style={{ flex: '1 1 0', minWidth: 0 }}>
              {/* <div style={{ padding: '1rem', borderBottom: '1px solid var(--ion-color-light-shade)', background: 'white' }}> */}
              <InspectionChecklist
                checklistItems={inspectionData.checklistItems}
                globalFiles={inspectionData.existingResources}
                formData={formData.checklistItems}
                onItemChange={handleChecklistItemChange}
                readOnly={isReadOnlyMode}
              />

              <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <IonItem>
                  <IonLabel position="stacked">Comments</IonLabel>
                  <IonTextarea
                    value={formData.comments}
                    placeholder="Enter inspection comments"
                    rows={3}
                    disabled={isReadOnlyMode}
                    onIonInput={(event) => handleCommentsChange(event.detail.value ?? '')}
                  />
                </IonItem>

                <IonItem>
                  <AttachmentsSection
                    title="Inspection Attachments"
                    attachments={formData.attachments}
                    onAddAttachment={handleAddInspectionAttachment}
                    onDelete={handleDeleteInspectionAttachment}
                    onFileUpdate={handleInspectionAttachmentFileUpdate}
                    onLinkUpdate={handleInspectionAttachmentLinkUpdate}
                    onExistingFileSelect={handleInspectionAttachmentExistingFileSelect}
                    onDescriptionUpdate={handleInspectionAttachmentDescriptionUpdate}
                    existingFiles={inspectionData.existingResources}
                    readOnly={isReadOnlyMode}
                  />
                </IonItem>

                <IonItem lines="none"  style={{ maxWidth: '340px', marginTop: '1rem' }}>
                  <IonCheckbox
                    checked={formData.isExceptional}
                    disabled={isReadOnlyMode}
                    onIonChange={(event) => handleExceptionalChange(event.detail.checked)}
                  />
                  <IonLabel style={{ marginLeft: '12px' }}>Flag this inspection as exceptional</IonLabel>
                </IonItem>
              </div>
            </div>
          </div>
        </IonContent>

        { internalMode === 'delete' ? (
          <ModalFooter
            cancelLabel="Cancel"
            actionLabel={isDeleting ? 'Deleting...' : 'Delete'}
            onCancel={() => setInternalMode('edit')}
            onAction={() => setShowDeleteAlert(true)}
            isCancelDisabled={isDeleting}
            isActionDisabled={isDeleting}
            isActionLoading={isDeleting}
            actionColor="danger"
            cancelIcon={arrowBack}
            actionIcon={trashOutline}
          />
        ) : (
          <ModalFooter
            cancelLabel="Cancel"
            actionLabel={loading ? 'Saving...' : 'Save Changes'}
            onCancel={handleClose}
            onAction={handleSave}
            isCancelDisabled={loading || autoSaving}
            isActionDisabled={loading || autoSaving || !hasUnsavedChanges()}
            isActionLoading={loading}
            cancelIcon={arrowBack}
            actionIcon={checkmark}
          />
        )}
      </IonModal>

      {/* Exit Confirmation Alert */}
      <IonAlert
        isOpen={showExitAlert}
        onDidDismiss={() => setShowExitAlert(false)}
        header="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close without saving?"
        buttons={[
          {
            text: 'Stay',
            role: 'cancel',
          },
          {
            text: 'Leave',
            handler: handleForceClose,
          },
        ]}
      />

      {/* Delete Confirmation Alert */}
      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => setShowDeleteAlert(false)}
        header="Delete Inspection"
        message="Are you sure you want to delete this inspection? This action cannot be undone."
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
          },
          {
            text: 'Delete',
            role: 'destructive',
            handler: handleDelete,
          },
        ]}
      />

      {/* Toast for notifications */}
      <Toaster
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />
    </>
  );
};

export default InspectionModal;